// NOTE: NSAI-5881 — validation for the /app-config deployment block.
//
// The block is untrusted input: the mobile app can be pointed at any server,
// so whatever a server says about itself is checked here, once, at the decode
// boundary, before a client acts on it. Three rules from the multi-tenant
// design: only https URLs survive, an unknown flavor reads as the default
// brand, and one entry that does not fit is dropped on its own instead of
// taking the rest of the block with it. A block whose core shape is wrong is
// rejected whole, which leaves the client exactly where an older server would.

import { z } from "zod";

import type { IAppConfigDeployment } from "../types/app-config";
import { ESocialAuthProvider } from "../types/auth";

const DEFAULT_FLAVOR_KEY = "nodeshift";

// NOTE: Major.minor.patch only — the mobile version gate compares numbers, and
// a pre-release suffix it cannot order is better ignored than guessed at.
const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

const SOCIAL_AUTH_PROVIDERS: ReadonlySet<string> = new Set(
  Object.values(ESocialAuthProvider),
);

const isSocialAuthProvider = (value: unknown): value is ESocialAuthProvider =>
  typeof value === "string" && SOCIAL_AUTH_PROVIDERS.has(value);

const optionalHttpsUrlSchema = z
  .string()
  .url()
  .refine((value) => value.startsWith("https://"))
  .optional()
  .catch(undefined);

const optionalTextSchema = z.string().min(1).optional().catch(undefined);

const aiProviderSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  isSelfHosted: z.boolean(),
});

const deploymentSchema = z.object({
  isOnPremise: z.boolean(),
  flavor: z.object({
    key: z.enum(["nodeshift", "oplot"]).catch(DEFAULT_FLAVOR_KEY),
    appName: z.string().min(1),
    companyName: z.string().min(1),
    defaultLanguage: z.string().min(1),
    logoUrl: optionalHttpsUrlSchema,
  }),
  auth: z.object({
    isSignUpEnabled: z.boolean(),
    isSsoVisible: z.boolean(),
    // NOTE: A field that is not even a list reads as "no social sign-in" —
    // the direction that cannot offer a flow the server refuses.
    socialProviders: z
      .array(z.unknown())
      .catch([])
      .transform((values) => values.filter(isSocialAuthProvider)),
    isPasswordResetSelfService: z.boolean(),
  }),
  // NOTE: Deliberately no field-level fallback here, unlike socialProviders. An
  // empty list reads as "every model is self-hosted" to the AI disclosure, the
  // wrong direction to be wrong in, so a providers field that is not a list
  // fails the whole block and leaves the client on its own defaults.
  ai: z.object({
    providers: z.array(z.unknown()).transform((values) =>
      values.flatMap((value) => {
        const parsed = aiProviderSchema.safeParse(value);

        return parsed.success ? [parsed.data] : [];
      }),
    ),
  }),
  analytics: z.object({
    isEnabled: z.boolean(),
    posthogKey: optionalTextSchema,
    posthogHost: optionalHttpsUrlSchema,
  }),
  legal: z.object({
    privacyPolicyUrl: optionalHttpsUrlSchema,
    supportEmail: z.string().email().optional().catch(undefined),
    termsUrl: optionalHttpsUrlSchema,
  }),
  policies: z.object({
    isAccountDeletionEnabled: z.boolean(),
  }),
  minMobileAppVersion: z
    .string()
    .regex(SEMVER_PATTERN)
    .optional()
    .catch(undefined),
});

// NOTE: Returns undefined for anything that is not a usable block — including
// a server that sent none at all — so callers have one "no manifest" case.
export const parseAppConfigDeployment = (
  value: unknown,
): IAppConfigDeployment | undefined => {
  const parsed = deploymentSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
};
